import { RouteRecordRaw, createRouter, createWebHistory } from "vue-router";

import { flushAll } from "@/composables/pending-flush";
import type * as Layouts from "@/layouts";
import mixpanel from "@/lib/mixpanel";
import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "@/stores/authentication-store";

declare module "vue-router" {
    interface RouteMeta {
        requiresAuth?: boolean;
        requiresOrg?: boolean;
        requiresStek?: boolean;
        breadcrumbs?: string[] | "auto";
        layout?: keyof typeof Layouts;
        mode?: "business" | "live" | "dev";
        hideSidebar?: boolean;
        fullBleed?: boolean;
        preventRedirect?: boolean;
    }
}

const baseRoutes: Array<RouteRecordRaw> = [
    {
        path: "/home",
        name: "home",
        component: () => import("@/views/Home.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/login",
        name: "login",
        component: () => import("@/views/Login.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/oauth/authorize",
        name: "oauth-authorize",
        component: () => import("@/views/OAuthAuthorize.vue"),
        meta: {
            layout: "LoggedOutPageLayout",
            requiresAuth: true,
        },
    },
    {
        // Sign-on hop into a live dev-stack preview: authenticates against
        // bekend's dev-preview enter endpoint, then redirects to the minted
        // preview-gate URL. Preview links shared by the dev agent point here.
        path: "/preview/:host",
        name: "previewEnter",
        component: () => import("@/views/PreviewEnter.vue"),
        meta: {
            layout: "LoggedOutPageLayout",
            requiresAuth: true,
        },
    },
    {
        path: "/signup",
        name: "signup",
        component: () => import("@/views/SignUp.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/privacy-policy",
        name: "privacyPolicy",
        component: () => import("@/views/PrivacyPolicy.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/terms-of-use",
        name: "termsOfUse",
        component: () => import("@/views/TermsOfUse.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/forgot-password",
        name: "forgotPassword",
        component: () => import("@/views/ForgotPassword.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/biz",
        name: "biz",
        component: () => import("@/views/Biz.vue"),
    },
    {
        path: "/ops",
        name: "ops",
        component: () => import("@/views/Ops.vue"),
    },
    {
        path: "/domain",
        name: "domain",
        component: () => import("@/views/Domain.vue"),
    },
    {
        path: "/product",
        name: "product",
        component: () => import("@/views/Product.vue"),
    },
    {
        path: "/process",
        name: "process",
        component: () => import("@/views/Process.vue"),
    },
    {
        path: "/application",
        name: "application",
        component: () => import("@/views/Application.vue"),
    },
    {
        path: "/service",
        name: "service",
        component: () => import("@/views/Service.vue"),
    },
    {
        path: "/docs",
        name: "docsHome",
        component: () => import("@/views/DocsHome.vue"),
        meta: {
            layout: "ApplicationLayout",
            hideSidebar: true,
        },
    },
    {
        path: "/help",
        name: "help",
        component: () => import("@/views/Help.vue"),
        meta: {
            hideSidebar: true,
        },
    },
    {
        path: "/docs/core/live-mode-events",
        name: "liveModeEventsDocs",
        component: () => import("@/views/LiveModeEventsDocs.vue"),
        meta: {
            layout: "ApplicationLayout",
            hideSidebar: true,
        },
    },
    {
        path: "/docs/live-mode-events",
        redirect: "/docs/core/live-mode-events",
    },
    {
        path: "/settings",
        component: () => import("@/views/Profile.vue"),
        meta: {
            requiresAuth: true,
            hideSidebar: true,
        },
        children: [
            {
                path: "",
                name: "settings",
                redirect: { name: "settings-general" },
            },
            {
                path: "general",
                name: "settings-general",
                component: () => import("@/views/Profile.vue"),
                meta: {
                    requiresAuth: true,
                    hideSidebar: true,
                },
            },
            {
                path: "organizations",
                name: "settings-organizations",
                component: () => import("@/views/Profile.vue"),
                meta: {
                    requiresAuth: true,
                    hideSidebar: true,
                },
            },
            {
                path: "security",
                name: "settings-security",
                component: () => import("@/views/Profile.vue"),
                meta: {
                    requiresAuth: true,
                    hideSidebar: true,
                },
            },
            {
                path: "api-keys",
                name: "settings-api-keys",
                component: () => import("@/views/Profile.vue"),
                meta: {
                    requiresAuth: true,
                    hideSidebar: true,
                },
            },
        ],
    },
    {
        path: "/oauth/callback",
        name: "oauthCallback",
        component: () => import("@/views/OAuthCallback.vue"),
        meta: {
            requiresStek: false,
            requiresAuth: true,
            requiresOrg: false,
        },
    },
    {
        path: "/profile",
        redirect: "/settings",
    },
    {
        path: "/profile/org/:orgId",
        redirect: (to) => `/settings/org/${to.params.orgId}`,
    },
    {
        path: "/profile/:orgId/:stekId/integrations",
        redirect: (to) =>
            `/settings/${to.params.orgId}/${to.params.stekId}/integrations`,
    },
    {
        path: "/activity-metrics",
        name: "activityMetrics",
        component: () => import("@/views/ActivityMetrics.vue"),
        meta: {
            requiresAuth: true,
            requiresOrg: false, // Platform-wide
            requiresStek: false,
            hideSidebar: true,
            layout: "ApplicationLayout",
        },
    },
    {
        path: "/:orgName?",
        name: "organizationPage",
        component: () => import("@/views/OrgInst.vue"),
        meta: {
            requiresAuth: true,
            requiresOrg: true,
            requiresStek: true,
            layout: "ApplicationLayout",
        },
        children: [
            {
                path: "",
                name: "organizationHome",
                component: () => import("@/views/OrganizationHome.vue"),
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                    hideSidebar: true,
                },
            },
            {
                path: "stek/create",
                name: "createStek",
                component: () => import("@/views/CreateStek.vue"),
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                    hideSidebar: true,
                },
            },
            {
                path: "manage/steks",
                name: "stekManagement",
                component: () => import("@/views/StekManagement.vue"),
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                    hideSidebar: true,
                },
            },
            {
                path: "manage/whats-new",
                name: "whatsNewManagement",
                component: () => import("@/views/WhatsNewManagement.vue"),
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                    hideSidebar: true,
                },
            },
            {
                path: "user",
                name: "userManagement",
                component: () => import("@/views/UserManagement.vue"),
                meta: {
                    requiresStek: false,
                    requiresAuth: true,
                    requiresOrg: true,
                    hideSidebar: true,
                },
            },
            {
                path: "steks",
                component: undefined,
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: true,
                },
                children: [
                    {
                        path: ":stekName",
                        component: undefined,
                        children: [
                            {
                                path: "integrations",
                                name: "integrationManagement",
                                component: () =>
                                    import("@/views/IntegrationManagement.vue"),
                                meta: {
                                    hideSidebar: true,
                                },
                            },
                            {
                                path: "settings",
                                name: "stekSettings",
                                component: () =>
                                    import("@/views/StekSettings.vue"),
                                meta: {
                                    hideSidebar: true,
                                },
                            },
                            {
                                // Thread-scoped, full-page conflict resolution
                                // ("Reconcile"); reads the thread via the app's
                                // usual `?thread=<id>` query convention (see
                                // ThreadDrawer/ApplicationLayout), not a path param.
                                path: "reconcile",
                                name: "reconcile",
                                component: () =>
                                    import("@/views/ReconcilePage.vue"),
                                // Keeps the sidebar (+ its toggle) and breadcrumbs,
                                // but opts out of `.page`'s --page-margin inset so the
                                // top bar + flush left queue pane reach the edges.
                                meta: {
                                    breadcrumbs: "auto",
                                    fullBleed: true,
                                },
                            },
                            {
                                path: "",
                                alias: "biz/domain",
                                name: "stekPage",
                                component: () => import("@/views/StekPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "strategy",
                                redirect: { name: "strategyVision" },
                            },
                            {
                                path: "strategy/vision",
                                name: "strategyVision",
                                component: () =>
                                    import("@/views/strategy/StrategyVisionPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "strategy/objectives",
                                name: "strategyObjectives",
                                component: () =>
                                    import("@/views/strategy/StrategyObjectivesPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "strategy/horizons",
                                name: "strategyHorizons",
                                component: () =>
                                    import("@/views/strategy/StrategyHorizonsPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live",
                                name: "livePage",
                                component: () =>
                                    import("@/live/views/Page.vue"),
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/domains",
                                name: "liveDomainsPage",
                                component: () =>
                                    import("@/live/views/DomainsPage.vue"),
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/domain/:domainId",
                                name: "liveDomainPage",
                                component: () =>
                                    import("@/live/views/Page.vue"),
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/products",
                                name: "liveProductsPage",
                                component: () =>
                                    import("@/live/views/ProductsPage.vue"),
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/product/:productId",
                                name: "liveProductPage",
                                component: () =>
                                    import("@/live/views/Page.vue"),
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/processes",
                                name: "liveProcessesPage",
                                component: () =>
                                    import("@/live/views/ProcessesPage.vue"),
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/process/:processId",
                                name: "liveProcessPage",
                                component: () =>
                                    import("@/live/views/Page.vue"),
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/process/:processId/instance/:instanceId",
                                name: "processInstancePage",
                                component: () =>
                                    import("@/live/views/ProcessInstancePage.vue"),
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/compliance-evidence",
                                name: "complianceEvidencePage",
                                component: () =>
                                    import("@/live/views/ComplianceEvidencePage.vue"),
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "live/compliance-evidence-detail",
                                name: "complianceEvidenceDetailPage",
                                component: () =>
                                    import("@/live/views/ComplianceEvidenceDetailPage.vue"),
                                meta: {
                                    mode: "live",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "dev",
                                name: "devPage",
                                component: () => import("@/views/DevPage.vue"),
                                meta: {
                                    mode: "dev",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "dev/domain/:domainId",
                                name: "devDomainPage",
                                component: () =>
                                    import("@/views/DevDomainPage.vue"),
                                meta: {
                                    mode: "dev",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "dev/application/:applicationId",
                                name: "applicationPage",
                                component: () =>
                                    import("@/views/ApplicationPage.vue"),
                                meta: {
                                    mode: "dev",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "dev/application/:applicationId/application_entity/:applicationEntityId",
                                name: "applicationEntityPage",
                                component: () =>
                                    import("@/views/ApplicationEntityPage.vue"),
                                meta: {
                                    mode: "dev",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "domain/:domainId",
                                name: "domainPage",
                                component: () =>
                                    import("@/views/DomainPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "domain/:domainId/document/:artefactId?",
                                name: "domainDocumentPage",
                                component: () =>
                                    import("@/views/RedirectByIdPage.vue"),
                            },
                            {
                                path: "product/:productId",
                                name: "productPage",
                                component: () =>
                                    import("@/views/ProductPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "role/:roleId",
                                name: "rolePage",
                                component: () => import("@/views/RolePage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "business_entity/:businessEntityId",
                                name: "businessEntityPage",
                                component: () =>
                                    import("@/views/BusinessEntityPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "domain_term/:domainTermId",
                                name: "domainTermPage",
                                component: () =>
                                    import("@/views/DomainTermPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "product/:productId/document/:artefactId?",
                                name: "productDocumentPage",
                                component: () =>
                                    import("@/views/RedirectByIdPage.vue"),
                            },
                            {
                                path: "process/:processId",
                                name: "processPage",
                                component: () =>
                                    import("@/views/ProcessPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/process-definition/definition/:version?",
                                name: "processDefinitionPage",
                                component: () =>
                                    import("@/views/ProcessDefinitionPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/document/:artefactId?",
                                name: "processDocumentPage",
                                component: () =>
                                    import("@/views/RedirectByIdPage.vue"),
                            },
                            {
                                path: "process/:processId/task/:taskId",
                                name: "taskPage",
                                component: () =>
                                    import("@/views/RedirectByIdPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "process/:processId/task/:taskId/document/:artefactId?",
                                name: "taskDocumentPage",
                                component: () =>
                                    import("@/views/RedirectByIdPage.vue"),
                            },
                            {
                                path: ":elementType/:elementId",
                                name: "processElementPage",
                                component: () =>
                                    import("@/views/ProcessElementPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "document/:artefactId/:version?",
                                name: "documentPage",
                                component: () =>
                                    import("@/views/DocumentPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                path: "biz/domain/:domainName",
                                name: "domainDashboard",
                                component: () =>
                                    import("@/views/RedirectByNamePage.vue"),
                                children: [
                                    {
                                        path: "",
                                        name: "domainOverview",
                                        component: () =>
                                            import("@/views/RedirectByNamePage.vue"),
                                    },
                                    {
                                        path: "roles",
                                        name: "roles",
                                        component: () =>
                                            import("@/views/RedirectByNamePage.vue"),
                                    },
                                    {
                                        path: "entities",
                                        name: "entities",
                                        component: () =>
                                            import("@/views/RedirectByNamePage.vue"),
                                    },
                                    {
                                        path: "files/:filePath?",
                                        name: "domainFiles",
                                        component: () =>
                                            import("@/views/RedirectByNamePage.vue"),
                                    },
                                ],
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/:filePath?",
                                name: "productInst",
                                component: () =>
                                    import("@/views/RedirectByNamePage.vue"),
                                props: true,
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/process/:processName/:filePath?",
                                name: "processInst",
                                component: () =>
                                    import("@/views/RedirectByNamePage.vue"),
                                props: true,
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/process/:processName/tasks/:taskName/:filePath?",
                                name: "taskInst",
                                component: () =>
                                    import("@/views/RedirectByNamePage.vue"),
                                props: true,
                            },
                            {
                                name: "not-found",
                                path: "not-found",
                                component: () => import("@/views/NotFound.vue"),
                            },
                            {
                                path: ":pathMatch(.*)*",
                                component: () => import("@/views/NotFound.vue"),
                            },
                        ],
                    },
                    {
                        path: ":pathMatch(.*)*",
                        component: () => import("@/views/NotFound.vue"),
                    },
                ],
            },
        ],
    },
    {
        path: "/:pathMatch(.*)*",
        component: () => import("@/views/NotFound.vue"),
    },
];

const devRoute: RouteRecordRaw = {
    path: "/dev",
    name: "dev",
    component: () => import("@/views/Dev.vue"),
    meta: {
        requiresAuth: false,
        layout: "DevLayout",
    },
    children: [
        {
            path: "autologin",
            name: "autologin",
            component: () => import("@/views/AutoLogin.vue"),
            meta: {
                requiresAuth: false,
            },
        },
        {
            path: "code-preview",
            name: "code-preview",
            component: () => import("@/views/CodePreviewDemo.vue"),
            meta: {
                requiresAuth: false,
                layout: "DevLayout",
            },
        },
        {
            path: "spinner",
            name: "spinner-demo",
            component: () => import("@/views/SpinnerDemo.vue"),
            meta: {
                requiresAuth: false,
                layout: "DevLayout",
            },
        },
        {
            path: "task-evaluation",
            component: () => import("@/views/TaskEvaluation.vue"),
            meta: {
                requiresAuth: true,
                layout: "DevLayout",
            },
            children: [
                {
                    path: "",
                    name: "task-evaluation",
                    component: () => import("@/views/TaskEvaluationHome.vue"),
                },
                // Generic route pattern for all task types
                {
                    path: ":taskType",
                    name: "task-evaluation-list",
                    component: () => import("@/views/TaskEvaluationList.vue"),
                    props: (route) => {
                        const taskTypeMap = {
                            "generate-description": {
                                apiType: "generate_description",
                                name: "Generate Description",
                            },
                            "generate-definition-xml": {
                                apiType: "generate_definition_xml",
                                name: "Generate BPMN Definition XML",
                            },
                            "generate-ui": {
                                apiType: "generate_user_interface",
                                name: "Generate UI Component",
                            },
                        };
                        const taskInfo =
                            taskTypeMap[route.params.taskType as string];
                        return {
                            taskType: taskInfo.apiType,
                            taskName: taskInfo.name,
                        };
                    },
                },
                {
                    path: ":taskType/configure",
                    name: "task-evaluation-configure",
                    component: () =>
                        import("@/components/TaskEvaluation/GenericTaskConfiguration.vue"),
                    props: (route) => ({
                        taskType: route.params.taskType as string,
                    }),
                },
                {
                    path: ":taskType/:id",
                    name: "task-evaluation-detail",
                    component: () => import("@/views/TaskEvaluationList.vue"),
                    props: (route) => {
                        const taskTypeMap = {
                            "generate-description": {
                                apiType: "generate_description",
                                name: "Generate Description",
                            },
                            "generate-definition-xml": {
                                apiType: "generate_definition_xml",
                                name: "Generate BPMN Definition XML",
                            },
                            "generate-ui": {
                                apiType: "generate_user_interface",
                                name: "Generate UI Component",
                            },
                        };
                        const taskInfo =
                            taskTypeMap[route.params.taskType as string];
                        return {
                            taskType: taskInfo.apiType,
                            taskName: taskInfo.name,
                            evaluationId: route.params.id,
                        };
                    },
                },
            ],
        },
    ],
};

const routes = [...baseRoutes];
if (import.meta.env.MODE !== "production") {
    routes.push(devRoute);
}

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        window.scrollTo(0, 0);
    },
});

// Shared by both the one-time boot check below and the recurring
// `beforeEach` guard, so unauthenticated users always get sent to login
// with the same "come back here after signing in" query param, whether
// this is the very first page load or a later in-app (SPA) navigation.
function buildLoginRedirect(to: {
    name?: unknown;
    params?: unknown;
    query?: unknown;
}) {
    return {
        name: "login",
        query: {
            returnTo: encodeURIComponent(
                JSON.stringify({
                    name: to.name,
                    params: to.params,
                    query: to.query,
                })
            ),
        },
    };
}

export let routerIsReady = false;
// Make sure we can do something right after the router is ready.
router.isReady().then(async () => {
    const { authStatus } = useAuthenticationManager();
    const { isInitialized } = useAuthenticationStore();
    routerIsReady = true;

    const requiresAuth = router.currentRoute.value.meta.requiresAuth;
    const currentRouteName = String(router.currentRoute.value.name);

    // Sometimes the authentication library is initialized before the router is,
    // in those cases we still need to redirect to the right pages.
    await isInitialized;
    switch (authStatus.value) {
        case AuthStatus.SIGNED_IN: {
            if (
                ["login", "signup", "forgotPassword"].includes(currentRouteName)
            ) {
                router.push("/");
            }
            break;
        }
        case AuthStatus.MFA_REQUIRED:
        case AuthStatus.MFA_ENROLLMENT_REQUIRED:
        case AuthStatus.EMAIL_VERIFICATION_REQUIRED: {
            if (currentRouteName !== "login") {
                router.push(buildLoginRedirect(router.currentRoute.value));
            }
            break;
        }
        case AuthStatus.NOT_SIGNED_IN: {
            // Special case for root path: redirect to /home instead of /login
            if (router.currentRoute.value.fullPath === "/") {
                router.push("/home");
            } else if (requiresAuth) {
                router.push(buildLoginRedirect(router.currentRoute.value));
            }
            break;
        }
    }
});

const AUTH_RECORDING_ROUTES = ["login", "signup", "forgotPassword"];

// First guard — flush all pending throttled writes before navigation so that
// the sync-store's router.beforeEach can pick up the queued entries.
router.beforeEach(() => {
    flushAll();
});

router.beforeEach(async (guard) => {
    // Start recording when entering auth flows so login and MFA setup are captured.
    // Stop recording when leaving auth routes so sessions don't extend into the app.
    if (AUTH_RECORDING_ROUTES.includes(String(guard.name))) {
        mixpanel.startSessionRecording();
    } else {
        mixpanel.stopSessionRecording();
    }

    mixpanel.trackPageview({
        "Page Name": guard.name,
        "Page URL": guard.path,
        ...guard.params,
    });

    const { isInitialized } = useAuthenticationStore();
    // There is a brief period in which the authentication library is still
    // initializing. In this period the authentication status is unknown.
    // As soon as the authentication status is known it will be updated in
    // the store, so we utilize a short-lived watcher so that we can wait
    // for the authentication status to be updated in the router guard.
    await isInitialized;

    // Special case for root path when not authenticated
    const { authStatus } = useAuthenticationManager();
    if (
        guard.fullPath === "/" &&
        authStatus.value === AuthStatus.NOT_SIGNED_IN
    ) {
        return { path: "/home" };
    }

    // Guard `requiresAuth` routes on every navigation, not just the initial
    // load — otherwise an in-app SPA navigation (e.g. router.push from a nav
    // menu) to a protected route while signed out would resolve/render
    // instead of bouncing to login.
    if (
        guard.meta.requiresAuth &&
        authStatus.value === AuthStatus.NOT_SIGNED_IN
    ) {
        return buildLoginRedirect(guard);
    }
});

export default router;
